import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { throttle } from "lodash";
import { DateTime } from "luxon";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Slider from "@material-ui/core/Slider";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import { makeStyles } from "@material-ui/core/styles";
import { P } from "./constants";
import { fmt } from "./helpers";
import { Card, Badge } from "./ui";
import { withInstallation } from "../../../components/InstallationContext";
import {
  getInsightsConfiguration,
  getInsightsConfigurationRecord,
} from "../../../lib/insightsFeature";
import {
  getChargesByDateQuery,
  insightsMonthlyRevparSummariesQuery,
  insightsNightAuditsQuery,
  insightsSanctionedPlateStatisticsQuery,
  updateInsightsNightAuditMutation,
  updateInsightsConfigurationMutation,
} from "../queries";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: 12 },

  // Green info banner (top)
  banner: { background: "#E6F4E8", borderRadius: 10, padding: "12px 14px", border: "0.5px solid #BFE3C6" },
  bannerTitle: { fontWeight: 700, color: "#2E7D32" },
  bannerBody: { color: "#2E7D32" },

  cardsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 },

  fieldBlock: { marginBottom: 12 },
  fieldBlockTight: { marginBottom: 4 },
  labelRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 },
  labelRowTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 },
  fieldLabel: { color: "var(--color-text-secondary)" },
  lockWrap: { display: "flex", alignItems: "center", gap: 6 },
  dollarRow: { display: "flex", alignItems: "center", gap: 4 },
  dollarSign: { color: "var(--color-text-secondary)" },

  // Numeric inputs (right aligned mono-ish)
  numInput: { width: 72 },
  numInputNarrow: { width: 60 },
  numInputWide: { width: 88 },
  auditInput: { width: 176 },
  numField: { textAlign: "right" },

  // Slider brand color
  slider: { color: P.nv },

  // Lock / reminder / action buttons
  smallBtn: { textTransform: "none", minWidth: 0, height: 40, padding: "0 8px" },
  pillBtn: { textTransform: "none", minWidth: 0, padding: "5px 10px", fontWeight: 600 },
  postBtn: { textTransform: "none", fontWeight: 700, padding: "8px 16px", minWidth: 52 },

  // Sanctioned card
  oppPanel: { background: "#E6F4E8", borderRadius: 7, padding: "9px 12px", marginBottom: 14, border: "0.5px solid #BFE3C6" },
  oppLabel: { color: "var(--color-text-secondary)", marginBottom: 2 },
  oppValue: { fontWeight: 700, color: "#2E7D32", letterSpacing: "-.02em", fontFamily: "var(--font-mono)" },
  oppNote: { color: "#2E7D32", marginTop: 2 },
  hint: { color: "var(--color-text-tertiary)", textAlign: "right", marginTop: 1 },
  breakdownTop: { borderTop: "0.5px solid var(--color-border-tertiary)", paddingTop: 10, marginTop: 4 },
  breakdownHeading: { fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 6, letterSpacing: ".04em" },
  scenarioRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "0.5px solid var(--color-border-tertiary)" },
  scenarioLabel: { color: "var(--color-text-secondary)" },
  scenarioVal: { fontWeight: 700, color: P.nv, fontFamily: "var(--font-mono)" },

  // Calculated output
  outGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 },
  outTile: { background: "var(--color-background-secondary)", borderRadius: 7, padding: 10 },
  outTileLabel: { color: "var(--color-text-secondary)", marginBottom: 2 },
  outTileVal: { fontWeight: 700 },
  assetSection: { borderTop: "0.5px solid var(--color-border-tertiary)", paddingTop: 8, marginBottom: 8 },
  assetHeading: { fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 6 },
  assetGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  outNotePanel: { background: "#E6F4E8", borderRadius: 7, padding: 10, border: "0.5px solid #3EAC4D" },
  outNoteTitle: { fontWeight: 600, color: "#2E7D32", marginBottom: 1 },
  outNoteBody: { color: "#2E7D32", opacity: 0.85 },

  // Night audit reminder banner
  reminderPanel: { background: "#E6F4E8", borderRadius: 8, padding: "10px 14px", marginBottom: 14, border: "0.5px solid #BFE3C6", display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" },
  reminderTitle: { fontWeight: 600, color: "#2E7D32", marginBottom: 3 },
  reminderBody: { color: "#2E7D32", marginBottom: 4 },
  reminderInputRow: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  emailInput: { width: 240 },
  linkNote: { color: "var(--color-text-tertiary)" },
  linkMono: { fontFamily: "var(--font-mono)", color: "#2E7D32" },
  reminderBtns: { display: "flex", gap: 6, flexShrink: 0 },

  auditGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  sectionHeadingRow: { fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: ".04em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 },
  sectionHeading: { fontWeight: 600, color: "var(--color-text-secondary)", letterSpacing: ".04em", marginBottom: 10 },
  requiredBadge: { fontWeight: 700, color: "#fff", background: P.nv, padding: "1px 6px", borderRadius: 3 },
  inputColumn: { display: "flex", flexDirection: "column", gap: 10 },
  requiredStar: { color: "#241C8A", marginLeft: 2 },
  errorNote: { color: "#241C8A", marginTop: 2 },

  revStack: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 },
  revRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: "var(--color-background-secondary)", borderRadius: 7, border: "0.5px solid var(--color-border-tertiary)" },
  revLabel: { color: "var(--color-text-secondary)", marginBottom: 1 },
  revSub: { color: "var(--color-text-tertiary)" },
  revValDrivo: { fontFamily: "var(--font-mono)", fontWeight: 700, color: P.nv },
  revValPms: { fontFamily: "var(--font-mono)", fontWeight: 700, color: "#3127B8" },
  revDivider: { height: "0.5px", background: "var(--color-border-tertiary)", margin: "4px 0" },
  totalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderRadius: 8 },
  totalRight: { display: "flex", alignItems: "center", gap: 10 },
  miniStatGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 },
  miniStat: { background: "var(--color-background-secondary)", borderRadius: 6, padding: "7px 9px", border: "0.5px solid var(--color-border-tertiary)" },
  miniStatLabel: { color: "var(--color-text-tertiary)", marginBottom: 2 },
  miniStatVal: { fontWeight: 700, fontFamily: "var(--font-mono)" },

  // Tables
  tableWrap: { overflowX: "auto" },
  auditTable: { minWidth: 700 },
  monthlyTable: { minWidth: 680 },
  th: { fontWeight: 600, color: "var(--color-text-secondary)", padding: "5px 10px", letterSpacing: ".04em", whiteSpace: "nowrap", borderBottom: "none" },
  thLeft: { textAlign: "left" },
  thRight: { textAlign: "right" },
  headRow: { borderBottom: "0.5px solid var(--color-border-tertiary)" },
  cell: { padding: "9px 10px", borderBottom: "0.5px solid var(--color-border-tertiary)" },
  cellMono: { fontFamily: "var(--font-mono)" },
  right: { textAlign: "right" },
  center: { textAlign: "center" },
  footRow: { borderTop: "1px solid var(--color-border-secondary)", background: "var(--color-background-secondary)" },
  footCell: { padding: "10px 10px", borderBottom: "none" },
  statusBadge: { fontWeight: 700, color: "#2E7D32", background: "#E6F4E8", padding: "2px 7px", borderRadius: 3 },
  statusBadgePosted: { fontWeight: 600, color: "#2E7D32", background: "#E6F4E8", padding: "2px 7px", borderRadius: 3 },

  // Monthly table
  monthPicker: { width: 170 },
  monthTh: { fontWeight: 600, color: "var(--color-text-secondary)", textAlign: "left", padding: "4px 8px", borderBottom: "0.5px solid var(--color-border-tertiary)" },
  monthCell: { padding: "7px 8px", borderBottom: "0.5px solid var(--color-border-tertiary)" },

  // Reusable static cell styling (tables) -- composed via template-literal className
  monoNumInput: { fontWeight: 600, color: P.nv, fontFamily: "var(--font-mono)" },
  drivoCell: { color: P.nv },
  pmsCell: { color: "#3127B8" },
  runningCell: { color: "#241C8A" },
  secondaryCell: { color: "var(--color-text-secondary)" },
  tertiaryCell: { color: "var(--color-text-tertiary)" },
  primaryCell: { color: "var(--color-text-primary)" },
  headlineCell: { color: P.headline },
  w500: { fontWeight: 500 },
  w600: { fontWeight: 600 },
  w700: { fontWeight: 700 },
  w800: { fontWeight: 800 },
});

const num = (value) => Number(value || 0);

const getApprovedDrivoChargeTotals = (chargesByDate) => {
  const stripeCharges = chargesByDate?.stripeCharges || [];
  const gateCharges = chargesByDate?.questCharges?.charges || [];
  const approvedStripeCharges = stripeCharges.filter((charge) => charge.approved === "true");
  const approvedGateCharges = gateCharges.filter((charge) => Boolean(charge.approved));
  const stripeTotal = approvedStripeCharges.reduce((sum, charge) => sum + num(charge.amountAud), 0);
  const gateTotal = approvedGateCharges.reduce((sum, charge) => sum + num(charge.amount) / 100, 0);

  return {
    count: approvedStripeCharges.length + approvedGateCharges.length,
    amount: Math.round((stripeTotal + gateTotal) * 100) / 100,
  };
};

const getDaysInMonth = (month) => {
  const parsed = DateTime.fromFormat(month || "", "yyyy-MM");
  return parsed.isValid ? parsed.daysInMonth : 0;
};

const getCurrentMonth = (timeZoneName) => DateTime
  .now()
  .setZone(timeZoneName || "Australia/Sydney")
  .toFormat("yyyy-MM");

const getMonthDateRange = (month, timeZoneName) => {
  const parsed = DateTime
    .fromFormat(month || "", "yyyy-MM", { zone: timeZoneName || "Australia/Sydney" });
  const selectedMonth = parsed.isValid
    ? parsed
    : DateTime.now().setZone(timeZoneName || "Australia/Sydney");

  return {
    fromDate: selectedMonth.startOf("month").toISODate(),
    toDate: selectedMonth.endOf("month").toISODate(),
  };
};

function OperationsTab({ facility, installation, updateFacility }) {
  const cls = useStyles();
  const insightsConfigRecord = getInsightsConfigurationRecord(facility);
  const insightsConfig = getInsightsConfiguration(facility) || {};
  const propertyConfig = insightsConfig.dataInput?.property || {};
  const revenueConfig = insightsConfig.dataInput?.revenueAssumptions || {};
  const defaultAdr = revenueConfig.defaultAdr ?? 320;
  const sanctionConfig = insightsConfig.dataInput?.sanctionedPlateOpportunityCost || {};
  const nightAuditConfig = insightsConfig.dataInput?.nightAudit || {};
  const [updateInsightsConfig] = useMutation(updateInsightsConfigurationMutation);
  const [updateInsightsNightAudit, { loading: postingAudit }] = useMutation(updateInsightsNightAuditMutation);
  const [selectedAuditMonth, setSelectedAuditMonth] = useState(() => getCurrentMonth(installation?.timeZoneName));
  const auditDateRange = useMemo(() => {
    return getMonthDateRange(selectedAuditMonth, installation?.timeZoneName);
  }, [installation?.timeZoneName, selectedAuditMonth]);
  const monthlyDateRange = useMemo(() => {
    const zone = installation?.timeZoneName || "Australia/Sydney";
    const toMonth = DateTime.now().setZone(zone).toFormat("yyyy-MM");
    const fromMonth = DateTime.now().setZone(zone).minus({ months: 5 }).toFormat("yyyy-MM");

    return { fromMonth, toMonth };
  }, [installation?.timeZoneName]);
  const {
    data: nightAuditData,
    loading: loadingNightAudits,
    refetch: refetchNightAudits,
  } = useQuery(insightsNightAuditsQuery, {
    variables: {
      facilityId: facility?.id,
      month: selectedAuditMonth,
      fromDate: auditDateRange.fromDate,
      toDate: auditDateRange.toDate,
    },
    skip: !facility?.id,
    fetchPolicy: "no-cache",
  });
  const {
    data: monthlyRevparData,
    loading: loadingMonthlyRevpar,
    refetch: refetchMonthlyRevpar,
  } = useQuery(insightsMonthlyRevparSummariesQuery, {
    variables: {
      facilityId: facility?.id,
      fromMonth: monthlyDateRange.fromMonth,
      toMonth: monthlyDateRange.toMonth,
    },
    skip: !facility?.id,
    fetchPolicy: "no-cache",
  });

  const [totalRooms, setTotalRooms] = useState(propertyConfig.totalRooms ?? 180);
  const [roomsSold, setRoomsSold] = useState(117);
  const [adr, setAdr] = useState(null);
  const [totalBays, setTotalBays] = useState(propertyConfig.totalParkingBays ?? 100);
  const [capRate, setCapRate] = useState(revenueConfig.capRatePercent ?? 6.5);
  const [fbRevPerRoom, setFbRevPerRoom] = useState(revenueConfig.fbRevenuePerOccupiedRoom ?? 45);
  const [otherRevPerRoom, setOtherRevPerRoom] = useState(revenueConfig.otherRevenuePerOccupiedRoom ?? 12);
  const [stdHourlyRate, setStdHourlyRate] = useState(sanctionConfig.standardHourlyRate ?? 22);
  const [roomsLocked, setRoomsLocked] = useState(Boolean(propertyConfig.totalRoomsLocked));
  const [baysLocked, setBaysLocked] = useState(Boolean(propertyConfig.totalParkingBaysLocked));
  const [standardHourlyRateLocked, setStandardHourlyRateLocked] = useState(Boolean(sanctionConfig.standardHourlyRateLocked));
  const [nightOcc, setNightOcc] = useState(null);
  const [pmsRevenue, setPmsRevenue] = useState(null);
  const [pmsCount, setPmsCount] = useState(null);
  const [auditSubmitAttempted, setAuditSubmitAttempted] = useState(false);
  const [nightAuditEmail, setNightAuditEmail] = useState(nightAuditConfig.reminderEmail || "nightaudit@grandhyatt.com.au");
  const [reminderActive, setReminderActive] = useState(nightAuditConfig.reminderEnabled ?? true);
  const [reminderSent, setReminderSent] = useState(false);
  const [selectedAuditId, setSelectedAuditId] = useState(null);
  const [showAllAudits, setShowAllAudits] = useState(false);
  const requiredInputs = [roomsSold, adr, fbRevPerRoom, otherRevPerRoom, nightOcc, pmsRevenue, pmsCount];
  const auditValid = requiredInputs.every((value) => value !== null && value !== "");
  const auditRows = nightAuditData?.insightsNightAudits || [];
  const monthlyRows = monthlyRevparData?.insightsMonthlyRevparSummaries || [];
  const selectedAudit = auditRows.find((item) => item.id === selectedAuditId) || auditRows[0] || null;
  const isSelectedAuditPosted = selectedAudit?.status === "POSTED";
  const selectedAuditDate = selectedAudit?.date || null;
  const chargeDateRange = useMemo(() => {
    if (!selectedAuditDate) return null;

    return {
      startDate: `${selectedAuditDate} 00:00`,
      endDate: `${selectedAuditDate} 23:59`,
    };
  }, [selectedAuditDate]);
  const {
    data: sanctionedStatsData,
    loading: loadingSanctionedStats,
  } = useQuery(insightsSanctionedPlateStatisticsQuery, {
    variables: {
      facilityId: facility?.id,
      date: selectedAuditDate,
    },
    skip: !facility?.id || !selectedAuditDate,
    fetchPolicy: "no-cache",
  });
  const {
    data: chargesByDateData,
    loading: loadingDrivoCharges,
  } = useQuery(getChargesByDateQuery, {
    variables: {
      input: {
        installationId: installation?.id,
        startDate: chargeDateRange?.startDate,
        endDate: chargeDateRange?.endDate,
      },
    },
    skip: !installation?.id || !chargeDateRange || isSelectedAuditPosted,
    fetchPolicy: "network-only",
  });
  const fetchedDrivoTotals = useMemo(
    () => getApprovedDrivoChargeTotals(chargesByDateData?.getChargesByDate),
    [chargesByDateData],
  );
  const drivoParkingRevenue = isSelectedAuditPosted
    ? num(selectedAudit?.drivoParkingRevenue)
    : fetchedDrivoTotals.amount;
  const drivoParkingChargeCount = isSelectedAuditPosted
    ? num(selectedAudit?.drivoParkingChargeCount)
    : fetchedDrivoTotals.count;
  const drivoChargesReady = isSelectedAuditPosted || !loadingDrivoCharges;

  const chargeableRatio = sanctionConfig.chargeableRatio ?? 0.7;
  const sanctionedBayHours = num(sanctionedStatsData?.insightsSanctionedPlateStatistics?.sanctionedPlateHours);
  const sanctionedOppCost = Math.round(sanctionedBayHours * stdHourlyRate * chargeableRatio);

  async function saveInsightsConfiguration(patch) {
    if (!insightsConfigRecord) {
      console.error("Insights feature configuration is not present for this facility.");
      return;
    }

    const { data } = await updateInsightsConfig({
      variables: { facilityId: facility.id, patch },
    });
    const saved = data?.updateInsightsConfiguration;

    if (saved && updateFacility) {
      const existing = facility.featureFacilityProviderConfigurations || [];
      const matchesRecord = (item) => (
        item.id === saved.id
        || item.id === insightsConfigRecord.id
        || item.feature?.name === "INSIGHTS"
      );
      updateFacility({
        featureFacilityProviderConfigurations: existing.some(matchesRecord)
          ? existing.map((item) => matchesRecord(item) ? { ...item, ...saved } : item)
          : [...existing, { ...insightsConfigRecord, ...saved }],
      });
    }
  }

  async function toggleRoomsLock() {
    const nextLocked = !roomsLocked;
    setRoomsLocked(nextLocked);
    await saveInsightsConfiguration({
      dataInput: {
        property: {
          totalRooms,
          totalRoomsLocked: nextLocked,
        },
      },
    });
  }

  async function toggleBaysLock() {
    const nextLocked = !baysLocked;
    setBaysLocked(nextLocked);
    await saveInsightsConfiguration({
      dataInput: {
        property: {
          totalParkingBays: totalBays,
          totalParkingBaysLocked: nextLocked,
        },
      },
    });
  }

  async function toggleStandardHourlyRateLock() {
    const nextLocked = !standardHourlyRateLocked;
    setStandardHourlyRateLocked(nextLocked);
    await saveInsightsConfiguration({
      dataInput: {
        sanctionedPlateOpportunityCost: {
          standardHourlyRate: stdHourlyRate,
          standardHourlyRateLocked: nextLocked,
        },
      },
    });
  }

  const throttledSaveNightAuditEmail = useMemo(
    () =>
      throttle((reminderEmail) => {
        saveInsightsConfiguration({
          dataInput: {
            nightAudit: {
              reminderEmail,
            },
          },
        });
      }, 800),
    [insightsConfigRecord?.id, insightsConfig],
  );

  useEffect(() => {
    return () => {
      throttledSaveNightAuditEmail.flush();
      throttledSaveNightAuditEmail.cancel();
    };
  }, [throttledSaveNightAuditEmail]);

  function onNightAuditEmailChange(e) {
    const nextEmail = e.target.value;
    setNightAuditEmail(nextEmail);
    throttledSaveNightAuditEmail(nextEmail);
  }

  async function toggleReminderActive() {
    const nextReminderActive = !reminderActive;
    setReminderActive(nextReminderActive);
    await saveInsightsConfiguration({
      dataInput: {
        nightAudit: {
          reminderEnabled: nextReminderActive,
        },
      },
    });
  }

  useEffect(() => {
    if (auditRows.length > 0 && !auditRows.some((audit) => audit.id === selectedAuditId)) {
      setSelectedAuditId(auditRows[0].id);
    }
  }, [auditRows, selectedAuditId]);

  useEffect(() => {
    if (!selectedAudit) return;

    setRoomsSold(selectedAudit.roomsSold ?? null);
    setAdr(selectedAudit.averageDailyRate ?? null);
    setFbRevPerRoom(selectedAudit.fnbRevenuePerRoom ?? null);
    setOtherRevPerRoom(selectedAudit.otherRevenuePerRoom ?? null);
    setNightOcc(selectedAudit.hotelOccupancyPercent ?? null);
    setPmsRevenue(selectedAudit.pmsParkingRevenue ?? null);
    setPmsCount(selectedAudit.pmsParkingChargeCount ?? null);
    setAuditSubmitAttempted(false);
  }, [selectedAudit?.id]);

  async function postSelectedAudit() {
    setAuditSubmitAttempted(true);
    if (!selectedAudit?.id || !auditValid || !drivoChargesReady || isSelectedAuditPosted) return;

    const { data } = await updateInsightsNightAudit({
      variables: {
        id: selectedAudit.id,
        input: {
          roomsSold,
          averageDailyRate: adr,
          fnbRevenuePerRoom: fbRevPerRoom,
          otherRevenuePerRoom: otherRevPerRoom,
          hotelOccupancyPercent: nightOcc,
          pmsParkingRevenue: pmsRevenue,
          pmsParkingChargeCount: pmsCount,
          drivoParkingRevenue,
          drivoParkingChargeCount,
          status: "POSTED",
        },
      },
    });
    const updatedAudit = data?.updateInsightsNightAudit;

    if (updatedAudit) setSelectedAuditId(updatedAudit.id);
    await refetchNightAudits();
    await refetchMonthlyRevpar();
  }

  const occ = totalRooms > 0 ? Math.round(num(roomsSold) / totalRooms * 100) : 0;
  const revpar = Math.round(num(adr) * occ / 100);
  const parkRev = 18000;
  const parkRevPAR = totalRooms > 0 ? Math.round(parkRev / totalRooms / 12) : 0;
  const fbRevPAR = fbRevPerRoom * occ / 100;
  const trevpar = Math.round(revpar + parkRevPAR + fbRevPAR + otherRevPerRoom * occ / 100);
  const annualParkRev = parkRev * (365/12);
  const assetContrib = capRate > 0 ? Math.round(annualParkRev / (capRate / 100)) : 0;
  const valuePerBay = totalBays > 0 ? Math.round(assetContrib / totalBays) : 0;

  return (
    <Box className={cls.root}>
      <Paper elevation={0} className={cls.banner}>
        <Typography variant="body1" component="div" className={cls.bannerTitle}>
          Hotel Data Input
        </Typography>
        <Typography variant="body2" component="div" className={cls.bannerBody}>
          Enter hotel-side data DRIVO cannot access. Powers RevPAR, TRevPAR, and asset valuation calculations in the Overview tab.
        </Typography>
      </Paper>

      <Box className={cls.cardsGrid}>
        <Card title="Rooms & Rate" subtitle="Core hotel metrics for RevPAR">
          <Box className={cls.fieldBlock}>
            <Box className={cls.labelRow}>
              <Typography variant="body2" component="label" className={cls.fieldLabel}>
                Total Rooms in Hotel
              </Typography>
              <Box className={cls.lockWrap}>
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={totalRooms}
                  disabled={roomsLocked}
                  onChange={(event) => {
                    setTotalRooms(Math.min(1000, Math.max(0, Number(event.target.value) || 0)));
                  }}
                  inputProps={{ min: 0, max: 1000, className: cls.numField }}
                  className={cls.numInput}
                  style={{ opacity: roomsLocked ? 0.6 : 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className={cls.smallBtn}
                  onClick={toggleRoomsLock}
                  style={{
                    borderColor: roomsLocked ? "#2E7D32" : "var(--color-border-secondary)",
                    background: roomsLocked ? "#E6F4E8" : "transparent",
                    color: roomsLocked ? "#2E7D32" : "var(--color-text-tertiary)",
                    fontWeight: roomsLocked ? 600 : 400,
                  }}
                >
                  {roomsLocked ? "Locked" : "Lock"}
                </Button>
              </Box>
            </Box>
          </Box>
          <Box className={cls.fieldBlock}>
            <Box className={cls.labelRow}>
              <Typography variant="body2" component="label" className={cls.fieldLabel}>
                Total Parking Bays
              </Typography>
              <Box className={cls.lockWrap}>
                <TextField
                  variant="outlined"
                  size="small"
                  type="number"
                  value={totalBays}
                  disabled={baysLocked}
                  onChange={(event) => {
                    setTotalBays(Math.min(500, Math.max(0, Number(event.target.value) || 0)));
                  }}
                  inputProps={{ min: 0, max: 500, className: cls.numField }}
                  className={cls.numInput}
                  style={{ opacity: baysLocked ? 0.6 : 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  className={cls.smallBtn}
                  onClick={toggleBaysLock}
                  style={{
                    borderColor: baysLocked ? "#2E7D32" : "var(--color-border-secondary)",
                    background: baysLocked ? "#E6F4E8" : "transparent",
                    color: baysLocked ? "#2E7D32" : "var(--color-text-tertiary)",
                    fontWeight: baysLocked ? 600 : 400,
                  }}
                >
                  {baysLocked ? "Locked" : "Lock"}
                </Button>
              </Box>
            </Box>
          </Box>
          {[
            {
              label: "Rooms Sold Today",
              value: roomsSold,
              setValue: setRoomsSold,
              min: 0,
              max: totalRooms,
            },
            {
              label: "Average Daily Rate ($)",
              value: adr,
              setValue: setAdr,
              min: 0,
              max: 2000,
              placeholder: String(defaultAdr),
            },
          ].map(({ label, value, setValue, min, max, placeholder }) => {
            const showError = auditSubmitAttempted && (value === null || value === "");

            return (
              <Box key={label} className={cls.fieldBlock}>
                <Box className={cls.labelRowTop}>
                  <Typography
                    variant="body2"
                    component="label"
                    className={cls.fieldLabel}
                    style={{
                      color: showError ? "#241C8A" : "var(--color-text-secondary)",
                      fontWeight: showError ? 600 : 400,
                    }}
                  >
                    {label}<span className={cls.requiredStar}>*</span>
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={value === null ? "" : value}
                    placeholder={placeholder}
                    onChange={(event) => {
                      const nextValue = event.target.value === ""
                        ? null
                        : Math.min(max, Math.max(0, Number(event.target.value) || 0));

                      setValue(nextValue);
                    }}
                    inputProps={{ min: 0, max, className: cls.numField }}
                    className={cls.numInput}
                    error={showError}
                    style={{ background: showError ? "#FFECEB" : "transparent" }}
                  />
                </Box>
                {showError && (
                  <Typography variant="caption" component="div" className={cls.errorNote}>
                    This field is required to post the night audit
                  </Typography>
                )}
              </Box>
            );
          })}
        </Card>

        <Card title="Ancillary Revenue" subtitle="For TRevPAR and full yield picture">
          {[
            {
              label: "F&B Revenue per Room ($)",
              value: fbRevPerRoom,
              setValue: setFbRevPerRoom,
              min: 0,
              max: 500,
            },
            {
              label: "Other Revenue per Room ($)",
              value: otherRevPerRoom,
              setValue: setOtherRevPerRoom,
              min: 0,
              max: 200,
            },
          ].map(({ label, value, setValue, min, max }) => {
            const showError = auditSubmitAttempted && (value === null || value === "");

            return (
              <Box key={label} className={cls.fieldBlock}>
                <Box className={cls.labelRowTop}>
                  <Typography
                    variant="body2"
                    component="label"
                    className={cls.fieldLabel}
                    style={{
                      color: showError ? "#241C8A" : "var(--color-text-secondary)",
                      fontWeight: showError ? 600 : 400,
                    }}
                  >
                    {label}<span className={cls.requiredStar}>*</span>
                  </Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={value === null ? "" : value}
                    onChange={(event) => {
                      const nextValue = event.target.value === ""
                        ? null
                        : Math.min(max, Math.max(0, Number(event.target.value) || 0));

                      setValue(nextValue);
                    }}
                    inputProps={{ min, max, className: cls.numField }}
                    className={cls.numInput}
                    error={showError}
                    style={{ background: showError ? "#FFECEB" : "transparent" }}
                  />
                </Box>
                {showError && (
                  <Typography variant="caption" component="div" className={cls.errorNote}>
                    This field is required to post the night audit
                  </Typography>
                )}
              </Box>
            );
          })}
          <Box className={cls.fieldBlockTight}>
            <Box className={cls.labelRowTop}>
              <Typography variant="body2" component="label" className={cls.fieldLabel}>
                Cap Rate (%)
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                type="number"
                value={capRate}
                onChange={(event) => {
                  setCapRate(Math.min(15, Math.max(0, Number(event.target.value) || 0)));
                }}
                inputProps={{ min: 0, max: 15, step: 0.1, className: cls.numField }}
                className={cls.numInput}
              />
            </Box>
            <Slider
              className={cls.slider}
              min={0}
              max={15}
              step={0.1}
              value={capRate}
              onChange={(event, nextValue) => setCapRate(nextValue)}
            />
          </Box>
        </Card>

        <Card title="Sanctioned Plate Opportunity Cost" subtitle="Set rates to calculate revenue displaced by authorised vehicles">
          <Paper elevation={0} className={cls.oppPanel}>
            <Typography variant="caption" component="div" className={cls.oppLabel}>
              Estimated Opportunity Cost This Period
            </Typography>
            <Typography variant="h5" component="div" className={cls.oppValue}>
              {"$" + sanctionedOppCost.toLocaleString()}
            </Typography>
            <Typography variant="caption" component="div" className={cls.oppNote}>
              {loadingSanctionedStats
                ? "Loading sanctioned plate hours..."
                : `${sanctionedBayHours}h of sanctioned bay usage x $${stdHourlyRate}/hr standard rate x ${Math.round(chargeableRatio * 100)}% chargeable`}
            </Typography>
          </Paper>
          <Box className={cls.fieldBlock}>
            <Box className={cls.labelRowTop}>
              <Typography variant="body2" component="label" className={cls.fieldLabel}>
                Standard Hourly Rate ($)
              </Typography>
              <Box className={cls.lockWrap}>
                <Box className={cls.dollarRow}>
                  <Typography variant="body2" component="span" className={cls.dollarSign}>$</Typography>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={stdHourlyRate}
                    disabled={standardHourlyRateLocked}
                    onChange={(event) => {
                      setStdHourlyRate(Math.min(80, Math.max(0, Number(event.target.value) || 0)));
                    }}
                    inputProps={{ min: 0, max: 80, className: cls.numField }}
                    className={cls.numInputNarrow}
                    style={{ opacity: standardHourlyRateLocked ? 0.6 : 1 }}
                  />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  className={cls.smallBtn}
                  onClick={toggleStandardHourlyRateLock}
                  style={{
                    borderColor: standardHourlyRateLocked ? "#2E7D32" : "var(--color-border-secondary)",
                    background: standardHourlyRateLocked ? "#E6F4E8" : "transparent",
                    color: standardHourlyRateLocked ? "#2E7D32" : "var(--color-text-tertiary)",
                    fontWeight: standardHourlyRateLocked ? 600 : 400,
                  }}
                >
                  {standardHourlyRateLocked ? "Locked" : "Lock"}
                </Button>
              </Box>
            </Box>
            <Typography variant="caption" component="div" className={cls.hint}>
              {`$${stdHourlyRate}/hr x ${sanctionedBayHours}h = $${Math.round(sanctionedBayHours * stdHourlyRate).toLocaleString()} gross`}
            </Typography>
          </Box>
          <Box className={cls.breakdownTop}>
            <Typography variant="caption" component="div" className={cls.breakdownHeading}>
              BREAKDOWN BY SCENARIO
            </Typography>
            {[
              {
                label: "If all sanctioned hours charged at standard rate",
                value: Math.round(sanctionedBayHours * stdHourlyRate),
              },
              {
                label: "If 70% charged at standard rate",
                value: Math.round(sanctionedBayHours * stdHourlyRate * 0.7),
              },
            ].map(({ label, value }) => (
              <Box key={label} className={cls.scenarioRow}>
                <Typography variant="body2" component="span" className={cls.scenarioLabel}>
                  {label}
                </Typography>
                <Typography variant="body2" component="span" className={cls.scenarioVal}>
                  {"$" + value.toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>

        <Card title="Calculated Output" subtitle="Updates live as you adjust inputs">
          <Box className={cls.outGrid}>
            {[
              {
                label: "Occupancy",
                value: `${occ}%`,
                color: occ >= 80 ? "#241C8A" : occ >= 65 ? "#3127B8" : P.nv,
              },
              { label: "RevPAR", value: `$${revpar}`, color: P.nv },
              { label: "Parking RevPAR", value: `$${parkRevPAR}`, color: P.nv },
              { label: "TRevPAR", value: `$${trevpar}`, color: "#241C8A" },
            ].map(({ label, value, color }) => (
              <Box key={label} className={cls.outTile}>
                <Typography variant="caption" component="div" className={cls.outTileLabel}>
                  {label}
                </Typography>
                <Typography
                  variant="subtitle1"
                  component="div"
                  className={cls.outTileVal}
                  style={{ color }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box className={cls.assetSection}>
            <Typography variant="body2" component="div" className={cls.assetHeading}>
              ASSET VALUE CONTRIBUTION
            </Typography>
            <Box className={cls.assetGrid}>
              {[
                { label: "Parking Asset Value", value: fmt(assetContrib), color: "#3127B8" },
                { label: "Value per Bay", value: fmt(valuePerBay), color: "#241C8A" },
              ].map(({ label, value, color }) => (
                <Box key={label} className={cls.outTile}>
                  <Typography variant="caption" component="div" className={cls.outTileLabel}>
                    {label}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="div"
                    className={cls.outTileVal}
                    style={{ color }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Paper elevation={0} className={cls.outNotePanel}>
            <Typography variant="body2" component="div" className={cls.outNoteTitle}>
              Parking adds ${parkRevPAR}/room/night
            </Typography>
            <Typography variant="caption" component="div" className={cls.outNoteBody}>
              TRevPAR of ${trevpar} vs DRIVO network avg of ${Math.round(trevpar * 0.88)}. Asset value uses annualised parking NOI at {capRate}% cap rate.
            </Typography>
          </Paper>
        </Card>
      </Box>

      <Card title="Night Audit -- Parking Reconciliation" subtitle="Required fields -- complete before end of night audit">
        <Paper elevation={0} className={cls.reminderPanel}>
          <div>
            <Typography variant="body2" component="div" className={cls.reminderTitle}>
              12:00 AM Daily Reminder
            </Typography>
            <Typography variant="caption" component="div" className={cls.reminderBody}>
              Night auditor will receive a reminder email each night with a direct link to complete this form.
            </Typography>
            <Box className={cls.reminderInputRow}>
              <TextField
                variant="outlined"
                size="small"
                value={nightAuditEmail}
                onChange={onNightAuditEmailChange}
                placeholder="Night audit email..."
                className={cls.emailInput}
              />
            </Box>
          </div>
          <Box className={cls.reminderBtns}>
            <Button
              size="small"
              variant="outlined"
              className={cls.pillBtn}
              onClick={toggleReminderActive}
              style={{
                borderColor: reminderActive ? "#3EAC4D" : P.nv,
                background: reminderActive ? "#E6F4E8" : P.nv,
                color: reminderActive ? "#2E7D32" : "#fff",
              }}
            >
              {reminderActive ? "Reminder On" : "Reminder Off"}
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              className={cls.pillBtn}
              onClick={() => {
                setReminderSent(true);
                setTimeout(() => setReminderSent(false), 3000);
              }}
              style={{
                background: reminderSent ? "#E6F4E8" : P.nv,
                color: reminderSent ? "#2E7D32" : "#fff",
              }}
            >
              {reminderSent ? "Sent!" : "Send Now"}
            </Button>
          </Box>
        </Paper>

        <Box className={cls.auditGrid}>
          <div>
            <Typography variant="body2" component="div" className={cls.sectionHeadingRow}>
              NIGHT TEAM INPUT
              <Typography variant="caption" component="span" className={cls.requiredBadge}>REQUIRED</Typography>
            </Typography>
            <Box className={cls.inputColumn}>
              <div>
                <Box className={cls.labelRow}>
                  <Typography variant="body2" component="label" className={cls.fieldLabel}>
                    Night Audit Date
                  </Typography>
                  <Typography variant="body2" component="span" className={`${cls.monoNumInput} ${cls.primaryCell}`}>
                    {selectedAudit?.date || "-"}
                  </Typography>
                </Box>
              </div>
              {[
                {
                  label: "Hotel Occupancy (%)",
                  value: nightOcc,
                  setValue: setNightOcc,
                  min: 0,
                  max: 100,
                  step: 1,
                },
                {
                  label: "PMS Parking Charges -- Total $",
                  value: pmsRevenue,
                  setValue: setPmsRevenue,
                  min: 0,
                  max: 9999,
                  step: 1,
                },
                {
                  label: "PMS Parking Charges -- Count",
                  value: pmsCount,
                  setValue: setPmsCount,
                  min: 0,
                  max: 999,
                  step: 1,
                },
              ].map(({ label, value, setValue, min, max, step }) => {
                const isEmpty = value === null;
                const showError = auditSubmitAttempted && isEmpty;

                return (
                  <div key={label}>
                    <Box className={cls.labelRow}>
                      <Typography
                        variant="body2"
                        component="label"
                        className={cls.fieldLabel}
                        style={{
                          color: showError ? "#241C8A" : "var(--color-text-secondary)",
                          fontWeight: showError ? 600 : 400,
                        }}
                      >
                        {label}<span className={cls.requiredStar}>*</span>
                      </Typography>
                      <TextField
                        variant="outlined"
                        size="small"
                        type="number"
                        placeholder={showError ? "Required" : "--"}
                        value={value === null ? "" : value}
                        onChange={(event) => {
                          const nextValue = event.target.value === ""
                            ? null
                            : Math.min(max, Math.max(min, Number(event.target.value)));

                          setValue(nextValue);
                        }}
                        inputProps={{
                          min,
                          max,
                          step,
                          className: `${cls.numField} ${cls.monoNumInput}`,
                        }}
                        className={cls.auditInput}
                        style={{ background: showError ? "#FFECEB" : "transparent" }}
                        error={showError}
                      />
                    </Box>
                    {label.includes("(%)") && (
                      <Slider
                        className={cls.slider}
                        min={min}
                        max={max}
                        step={step}
                        value={value ?? 0}
                        onChange={(event, nextValue) => setValue(nextValue)}
                      />
                    )}
                    {showError && (
                      <Typography variant="caption" component="div" className={cls.errorNote}>
                        This field is required to post the night audit
                      </Typography>
                    )}
                  </div>
                );
              })}
            </Box>
          </div>

          <div>
            <Typography variant="body2" component="div" className={cls.sectionHeading}>PARKING REVENUE -- DAY PRIOR</Typography>
            <Box className={cls.revStack}>
              <Box className={cls.revRow}>
                <div>
                  <Typography variant="caption" component="div" className={cls.revLabel}>
                    DRIVO Collected
                  </Typography>
                  <Typography variant="caption" component="div" className={cls.revSub}>
                    {loadingDrivoCharges ? "Loading approved charges..." : `${drivoParkingChargeCount} approved transactions via DRIVO`}
                  </Typography>
                </div>
                <Typography variant="subtitle1" component="div" className={cls.revValDrivo}>
                  {loadingDrivoCharges ? "$--" : "$" + drivoParkingRevenue.toLocaleString()}
                </Typography>
              </Box>
              <Box className={cls.revRow}>
                <div>
                  <Typography variant="caption" component="div" className={cls.revLabel}>
                    Hotel PMS Collected
                  </Typography>
                  <Typography variant="caption" component="div" className={cls.revSub}>
                    {pmsCount !== null ? pmsCount : "--"} charges posted to PMS
                  </Typography>
                </div>
                <Typography variant="subtitle1" component="div" className={cls.revValPms}>
                  {pmsRevenue !== null ? "$" + pmsRevenue.toLocaleString() : "$--"}
                </Typography>
              </Box>
              <div className={cls.revDivider} />
              <Box
                className={cls.totalRow}
                style={{
                  background: auditValid && drivoChargesReady ? "#E6F4E8" : "var(--color-background-secondary)",
                  border: "0.5px solid " + (auditValid && drivoChargesReady ? "#BFE3C6" : "var(--color-border-tertiary)"),
                }}
              >
                <div>
                  <Typography
                    variant="body2"
                    component="div"
                    style={{
                      fontWeight: 700,
                      color: auditValid && drivoChargesReady ? P.nv : "var(--color-text-tertiary)",
                      marginBottom: 1,
                    }}
                  >
                    Total Parking Revenue
                  </Typography>
                  <Typography
                    variant="caption"
                    component="div"
                    style={{
                      color: auditValid && drivoChargesReady ? "#3127B8" : "var(--color-text-tertiary)",
                    }}
                  >
                    {auditValid && drivoChargesReady
                      ? `${drivoParkingChargeCount + (pmsCount || 0)} total transactions . Hotel occ: ${nightOcc}% . Audit date: ${selectedAudit?.date || "-"}`
                      : loadingDrivoCharges
                        ? "Loading DRIVO charges for audit date"
                        : "Enter all fields above to calculate"}
                  </Typography>
                </div>
                <Box className={cls.totalRight}>
                  <Typography
                    variant="h5"
                    component="div"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontWeight: 800,
                      color: auditValid && drivoChargesReady ? P.nv : "var(--color-border-secondary)",
                    }}
                  >
                    {auditValid && drivoChargesReady
                      ? "$" + (drivoParkingRevenue + (pmsRevenue || 0)).toLocaleString()
                      : "$----"}
                  </Typography>
                  <Button
                    className={cls.postBtn}
                    variant="contained"
                    disableElevation
                    disabled={!auditValid || !drivoChargesReady || !selectedAudit?.id || isSelectedAuditPosted || postingAudit}
                    onClick={postSelectedAudit}
                    style={{
                      background: isSelectedAuditPosted
                        ? "var(--color-background-secondary)"
                        : auditValid && drivoChargesReady
                          ? P.nv
                          : "#BFE3C6",
                      color: isSelectedAuditPosted
                        ? "var(--color-text-tertiary)"
                        : auditValid && drivoChargesReady
                          ? "#fff"
                          : "#2E7D32",
                      cursor: isSelectedAuditPosted ? "default" : "pointer",
                    }}
                  >
                    {isSelectedAuditPosted ? "Posted" : postingAudit ? "Posting..." : loadingDrivoCharges ? "Loading..." : "Post"}
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box className={cls.miniStatGrid}>
              {[
                [
                  "DRIVO Share",
                  auditValid && drivoChargesReady && (drivoParkingRevenue + (pmsRevenue || 0)) > 0
                    ? `${Math.round((drivoParkingRevenue / (drivoParkingRevenue + (pmsRevenue || 0))) * 100)}%`
                    : "--",
                ],
                [
                  "PMS Share",
                  auditValid && drivoChargesReady && (drivoParkingRevenue + (pmsRevenue || 0)) > 0
                    ? `${Math.round(((pmsRevenue || 0) / (drivoParkingRevenue + (pmsRevenue || 0))) * 100)}%`
                    : "--",
                ],
                [
                  "Rev / Occ Room",
                  auditValid && drivoChargesReady && nightOcc && totalRooms
                    ? "$" + Math.round((drivoParkingRevenue + (pmsRevenue || 0)) / (totalRooms * (nightOcc || 1) / 100))
                    : "--",
                ],
              ].map(([label, value]) => (
                <Box key={label} className={cls.miniStat}>
                  <Typography variant="caption" component="div" className={cls.miniStatLabel}>
                    {label}
                  </Typography>
                  <Typography
                    variant="body1"
                    component="div"
                    className={cls.miniStatVal}
                    style={{ color: auditValid && drivoChargesReady ? P.nv : "var(--color-text-tertiary)" }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </div>
        </Box>
      </Card>

      <Card
        title="Night Audit Log -- This Month"
        subtitle={`Daily parking revenue postings with running total for ${selectedAuditMonth}`}
        action={(
          <TextField
            variant="outlined"
            size="small"
            type="month"
            value={selectedAuditMonth}
            onChange={(event) => {
              const nextMonth = event.target.value || getCurrentMonth(installation?.timeZoneName);
              setSelectedAuditMonth(nextMonth);
              setSelectedAuditId(null);
              setShowAllAudits(false);
            }}
            className={cls.monthPicker}
          />
        )}
      >
        {(() => {
          const auditLog = auditRows;
          const visibleAuditLog = showAllAudits ? auditLog : auditLog.slice(0, 10);
          const canToggleAuditRows = auditLog.length > 10;
          const runningTotalsById = {};
          const runningTotals = [];
          let cum = 0;
          [...auditLog].reverse().forEach((audit) => {
            cum += num(audit.drivoParkingRevenue) + num(audit.pmsParkingRevenue);
            runningTotalsById[audit.id] = cum;
          });
          auditLog.forEach((audit) => runningTotals.push(runningTotalsById[audit.id] || 0));

          const grandDrivo = auditLog.reduce((sum, audit) => sum + num(audit.drivoParkingRevenue), 0);
          const grandPms = auditLog.reduce((sum, audit) => sum + num(audit.pmsParkingRevenue), 0);
          const grandTotal = grandDrivo + grandPms;
          const grandTransactions = auditLog.reduce(
            (sum, audit) => sum + num(audit.drivoParkingChargeCount) + num(audit.pmsParkingChargeCount),
            0,
          );

          if (loadingNightAudits) {
            return <Typography variant="body2" className={cls.tertiaryCell}>Loading night audits...</Typography>;
          }

          if (!auditLog.length) {
            return <Typography variant="body2" className={cls.tertiaryCell}>No night audits found for the selected month.</Typography>;
          }

          return (
            <>
              <Box className={cls.tableWrap}>
                <Table className={cls.auditTable}>
                  <TableHead>
                    <TableRow className={cls.headRow}>
                      {[
                        "Date",
                        "Rooms",
                        "ADR",
                        "Hotel Occ",
                        "DRIVO Txns",
                        "DRIVO Rev",
                        "PMS Txns",
                        "PMS Rev",
                        "Day Total",
                        "Running Total",
                        "Status",
                      ].map((heading) => {
                        const isRightAligned = [
                          "DRIVO Rev",
                          "PMS Rev",
                          "Day Total",
                          "Running Total",
                        ].includes(heading);

                        return (
                          <TableCell
                            key={heading}
                            className={`${cls.th} ${isRightAligned ? cls.thRight : cls.thLeft}`}
                          >
                            {heading}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAuditLog.map((audit) => {
                      const auditIndex = auditLog.findIndex((row) => row.id === audit.id);
                      const dayTotal = num(audit.drivoParkingRevenue) + num(audit.pmsParkingRevenue);
                      const isPending = audit.status === "PENDING";
                      const isSelected = selectedAudit?.id === audit.id;

                      return (
                        <TableRow
                          key={audit.id}
                          hover
                          onClick={() => setSelectedAuditId(audit.id)}
                          style={{
                            background: isSelected ? "#E6F4E8" : isPending ? "#E6F4E808" : "#E6F4E80A",
                            cursor: "pointer",
                          }}
                        >
                          <TableCell
                            className={cls.cell}
                            style={{
                              fontWeight: isPending || isSelected ? 600 : 400,
                              color: isPending ? P.nv : "var(--color-text-primary)",
                            }}
                          >
                            {audit.date}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.secondaryCell}`}>
                            {audit.roomsSold ?? "-"}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.secondaryCell}`}>
                            {audit.averageDailyRate != null ? "$" + num(audit.averageDailyRate).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.secondaryCell}`}>
                            {audit.hotelOccupancyPercent ?? "-"}{audit.hotelOccupancyPercent != null ? "%" : ""}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.center} ${cls.secondaryCell}`}>
                            {num(audit.drivoParkingChargeCount)}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.right} ${cls.w500} ${cls.drivoCell}`}>
                            {"$" + num(audit.drivoParkingRevenue).toLocaleString()}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.center} ${cls.secondaryCell}`}>
                            {num(audit.pmsParkingChargeCount)}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.right} ${cls.w500} ${cls.pmsCell}`}>
                            {"$" + num(audit.pmsParkingRevenue).toLocaleString()}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.right} ${cls.w700} ${cls.drivoCell}`}>
                            {"$" + dayTotal.toLocaleString()}
                          </TableCell>
                          <TableCell className={`${cls.cell} ${cls.cellMono} ${cls.right} ${cls.w700} ${cls.runningCell}`}>
                            {"$" + runningTotals[auditIndex].toLocaleString()}
                          </TableCell>
                          <TableCell className={cls.cell}>
                            {isPending ? (
                              <Typography variant="caption" component="span" className={cls.statusBadge}>
                                PENDING
                              </Typography>
                            ) : (
                              <Typography variant="caption" component="span" className={cls.statusBadgePosted}>
                                POSTED
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  <TableFooter>
                    <TableRow className={cls.footRow}>
                      <TableCell className={`${cls.footCell} ${cls.w700} ${cls.drivoCell}`} colSpan={4}>Month Total</TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.center} ${cls.w600} ${cls.secondaryCell}`}>
                        {auditLog.reduce((sum, audit) => sum + num(audit.drivoParkingChargeCount), 0)}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.right} ${cls.w700} ${cls.drivoCell}`}>
                        {"$" + grandDrivo.toLocaleString()}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.center} ${cls.w600} ${cls.secondaryCell}`}>
                        {auditLog.reduce((sum, audit) => sum + num(audit.pmsParkingChargeCount), 0)}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.right} ${cls.w700} ${cls.pmsCell}`}>
                        {"$" + grandPms.toLocaleString()}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.right} ${cls.w800} ${cls.drivoCell}`}>
                        {"$" + grandTotal.toLocaleString()}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.cellMono} ${cls.right} ${cls.w800} ${cls.runningCell}`}>
                        {"$" + grandTotal.toLocaleString()}
                      </TableCell>
                      <TableCell className={`${cls.footCell} ${cls.tertiaryCell}`}>
                        {grandTransactions} txns
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </Box>
              {canToggleAuditRows && (
                <Box mt={1} display="flex" justifyContent="center">
                  <Button size="small" variant="outlined" className={cls.pillBtn} onClick={() => setShowAllAudits((value) => !value)}>
                    {showAllAudits ? "Show less" : `Show more (${auditLog.length - 10})`}
                  </Button>
                </Box>
              )}
            </>
          );
        })()}
      </Card>

      <Card title="Monthly Data Entry" subtitle="Historical RevPAR tracking -- enter actuals for trend analysis">
        {loadingMonthlyRevpar ? (
          <Typography variant="body2" className={cls.tertiaryCell}>
            Loading monthly RevPAR history...
          </Typography>
        ) : !monthlyRows.length ? (
          <Typography variant="body2" className={cls.tertiaryCell}>
            No monthly RevPAR history found.
          </Typography>
        ) : (
          <Box className={cls.tableWrap}>
            <Table className={cls.monthlyTable}>
              <TableHead>
                <TableRow>
                  {[
                    "Month",
                    "Audits",
                    "Rooms Sold",
                    "ADR ($)",
                    "Occupancy",
                    "F&B RevPAR",
                    "Other RevPAR",
                    "RevPAR",
                    "DRIVO Parking RevPAR",
                    "Booking Parking RevPAR",
                    "TRevPAR",
                  ].map((heading) => (
                    <TableCell key={heading} className={cls.monthTh}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyRows.map((monthSummary) => (
                  <TableRow key={monthSummary.month}>
                    <TableCell className={`${cls.monthCell} ${cls.w500} ${cls.primaryCell}`}>
                      {monthSummary.label || monthSummary.month}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.secondaryCell}`}>
                      {num(monthSummary.postedAuditCount)}
                      /
                      {getDaysInMonth(monthSummary.month)}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.secondaryCell}`}>
                      {num(monthSummary.roomsSold).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.secondaryCell}`}>
                      {"$" + num(monthSummary.averageDailyRate).toLocaleString()}
                    </TableCell>
                    <TableCell className={cls.monthCell}>
                      <Badge color={num(monthSummary.occupancyPercent) >= 80 ? "#241C8A" : "#3127B8"}>
                        {num(monthSummary.occupancyPercent)}%
                      </Badge>
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.secondaryCell}`}>
                      {"$" + num(monthSummary.fnbRevenuePerRoom).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.secondaryCell}`}>
                      {"$" + num(monthSummary.otherRevenuePerRoom).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.w500} ${cls.headlineCell}`}>
                      {"$" + num(monthSummary.revPAR).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.drivoCell}`}>
                      {"$" + num(monthSummary.drivoParkingRevPAR).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.pmsCell}`}>
                      {"$" + num(monthSummary.bookingParkingRevPAR).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${cls.monthCell} ${cls.w600} ${cls.headlineCell}`}>
                      {"$" + num(monthSummary.totalRevPAR).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
}

export default withInstallation(OperationsTab);
